// src/components/PageLayout.jsx
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Box,
  MUILink,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Tooltip,
  useMediaQuery,
  HelpOutlineIcon,
} from "../ui";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import Search from "./Search";
import OrganizationSwitch from "./OrganizationSwitch";
import { NAV_ITEMS, isCurrentNav } from "./navItems";
import { useHeldDrawer } from "./walkthrough/drawerControl";
import { brand } from "../config/brand";
import { useOrganization } from "../hooks/useOrganization";
import "../styles/HomePage.css";
import secureLocalStorage from "react-secure-storage";
import { getDisplayName, isFederatedUser } from "../utils/authHelpers";

export const PageLayout = ({ children }) => {
  /* ---------- navigation helper ---------- */
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /* The selected organization's logo, top-left; the shell's if none. */
  const { organization } = useOrganization();
  const logo = organization?.logo ?? brand.logo;
  const logoAlt = organization?.logoAlt ?? brand.logoAlt;

  const clearLocal = () => secureLocalStorage.removeItem("form");

  const handleRowAction = (url, row) => {
    clearLocal();
    if (url && typeof url === "string" && url.startsWith("/")) {
      navigate(url, {
        replace: true,
        state: { timestamp: Date.now() },
      });
    } else if (row?.JobTicketID) {
      // fallback if url was null (not needed for PO list normally)
      navigate(`/EditJob/${row.JobTicketID}`, {
        replace: true,
        state: { timestamp: Date.now() },
      });
    }
  };

  /* ---------- user info ---------- */
  const [username, setUsername] = useState("");
  const [userGroups, setUserGroups] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
        setUsername(getDisplayName(user));
        setUserGroups(
          user.signInUserSession?.idToken?.payload["cognito:groups"] || [],
        );
      } catch {
        // Error handling
      }
    };
    fetchInfo();
  }, []);

  const handleSignOut = async () => {
    const federated = currentUser && isFederatedUser(currentUser);
    await Auth.signOut();
    // Federated users: Amplify redirects through Cognito logout endpoint
    // which also ends the Microsoft SSO session.
    // Non-federated users: redirect manually.
    if (!federated) {
      window.location.href = "/";
    }
  };

  /* Warehouse is editors' only, in the bar and in the drawer alike. */
  const canEdit =
    userGroups.includes("Admin") || userGroups.includes("Salesperson");
  const visibleNavItems = NAV_ITEMS.filter(
    (item) => !item.editorsOnly || canEdit,
  );

  /* ---------- responsive breakpoint ---------- */
  const theme = useTheme();
  /* The full bar needs about 1280px; narrower than that it squeezed labels
     onto two lines, so below it the bar collapses into the drawer. */
  const isDesktop = useMediaQuery(theme.breakpoints.up(1280));

  /* ---------- desktop Job Management dropdown ---------- */
  const [, setAnchorEl] = useState(null);
  const closeMenu = () => setAnchorEl(null);

  /* ---------- mobile drawer ---------- */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = (open) => () => setDrawerOpen(open);
  /* The walkthrough opens the drawer for the steps whose element is only in
     it, as a persistent drawer: a second modal would fight its card for focus
     (walkthrough/drawerControl.js). */
  const heldByWalkthrough = useHeldDrawer();

  /* common nav link helper (closes menus/drawer) */
  const handleNav = (to) => () => {
    setDrawerOpen(false);
    closeMenu();
    navigate(to);
  };

  /* -------------- RENDER -------------- */
  return (
    <>
      <AppBar
        position="fixed"
        className="no-print"
        sx={{ bgcolor: "primary.main" }}
      >
        <Toolbar sx={{ gap: 1.5, minHeight: 64 }}>
          {/* Hamburger (mobile only) */}
          {!isDesktop && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <MUILink component={RouterLink} to="/" underline="none">
            <Box
              component="img"
              src={logo}
              alt={logoAlt}
              sx={{
                height: 40,
                display: "block",
                /* Each organization's logo is drawn in its own primary, which
                   is also the bar's colour - Windward's green, Kimball's
                   black - so knock it out to white here. A logo that is not
                   one dark colour on transparent would need a config flag
                   (SPEC 3f); neither of these does. */
                filter: "brightness(0) invert(1)",
              }}
            />
          </MUILink>

          {/* Which organization every screen shows, beside the logo it
              changes; absent with one. Styled in OrganizationSwitch. */}
          {isDesktop && (
            <OrganizationSwitch sx={{ mr: 1 }} tour="organization-switch" />
          )}

          {/* Desktop nav links, one line each: a label that wraps doubles
              the bar's height on exactly the screens where it is tight. */}
          {isDesktop && (
            <Box
              component="nav"
              sx={{
                display: "flex",
                flexShrink: 0,
                gap: "2px",
                /* The page you are on is the lit link: a soft white fill
                   behind it, the rest slightly dimmed, so the bar says where
                   you are without a second glance. */
                "& .MuiButton-root": {
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  px: 1.25,
                  fontSize: "0.85rem",
                  borderRadius: 2,
                  color: "rgba(255,255,255,0.85)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#fff",
                  },
                  '&[aria-current="page"]': {
                    bgcolor: "rgba(255,255,255,0.16)",
                    color: "#fff",
                  },
                },
              }}
            >
              {visibleNavItems.map((item) => (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  color="inherit"
                  aria-current={
                    isCurrentNav(item, pathname) ? "page" : undefined
                  }
                  data-tour={item.tour && `nav-${item.tour}`}
                  onClick={item.editorsOnly ? clearLocal : undefined}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* spacer pushes right-side content over */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Desktop search - the one item that gives up width when the
              window is narrow */}
          {isDesktop && (
            <Box
              data-tour="bar-search"
              sx={{
                flex: "0 1 220px",
                minWidth: 110,
                "& .MuiInputBase-root": { bgcolor: "background.paper" },
              }}
            >
              <Search
                SearchName="Job Ticket ID"
                ButtonName="Job"
                Inquiry={0}
                Limit={5}
                RowButton="Edit"
                Action={handleRowAction}
                Small
              />
            </Box>
          )}

          {/* Desktop user info, then admin and sign-out side by side, so the
              block is two lines tall rather than three */}
          {isDesktop && (
            <>
              <Box sx={{ textAlign: "right", flexShrink: 0, maxWidth: 130 }}>
                <Typography variant="body2" noWrap title={username}>
                  User: {username}
                </Typography>
                <Typography variant="body2" noWrap>
                  Role: {userGroups.join(", ")}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {/* Help, as a small icon because the bar has room for nothing
                    wider: at 1280px it takes an Admin's job search to its
                    floor (docs/changes/help-module/SPEC.md §5). */}
                <Tooltip title="Help">
                  <IconButton
                    component={RouterLink}
                    to="/Help"
                    size="small"
                    color="inherit"
                    aria-label="Help"
                    data-tour="bar-help"
                    aria-current={pathname === "/Help" ? "page" : undefined}
                    sx={{
                      '&[aria-current="page"]': {
                        bgcolor: "rgba(255,255,255,0.16)",
                      },
                    }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {userGroups.includes("Admin") && (
                  <Button
                    component={RouterLink}
                    to="/AdminPage"
                    data-tour="admin-button"
                    size="small"
                    variant="outlined"
                    color="inherit"
                  >
                    Admin
                  </Button>
                )}
                <Button
                  size="small"
                  onClick={handleSignOut}
                  sx={{
                    px: 1.4,
                    whiteSpace: "nowrap",
                    bgcolor: "#2b2b2b",
                    color: "#fff",
                    "&:hover": { bgcolor: "#1d1d1d" },
                  }}
                >
                  Sign Out
                </Button>
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer --------------------------------------------------- */}
      {!isDesktop && (
        <Drawer
          anchor="left"
          variant={heldByWalkthrough ? "persistent" : "temporary"}
          open={drawerOpen || heldByWalkthrough}
          onClose={toggleDrawer(false)}
          className="no-print"
        >
          <Box
            sx={{
              width: 280,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
            role="presentation"
          >
            {/* logo in drawer */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MUILink
                component={RouterLink}
                to="/"
                underline="none"
                onClick={handleNav("/")}
              >
                <Box
                  component="img"
                  src={logo}
                  alt={logoAlt}
                  sx={{ height: 48 }}
                />
              </MUILink>
            </Box>
            <Divider />

            {/* Organization (mobile) - the same switch as the bar's */}
            <Box
              sx={{ px: 2, py: 1.5, display: "flex", justifyContent: "center" }}
            >
              <OrganizationSwitch tour="drawer-organization-switch" />
            </Box>
            <Divider />

            {/* Search (mobile) */}
            <Box sx={{ p: 2 }}>
              <Search
                SearchName="Job Ticket ID"
                ButtonName="Job"
                Inquiry={0}
                Limit={5}
                RowButton="Edit"
                Action={handleRowAction}
                Small
              />
            </Box>
            <Divider />

            <List
              dense
              disablePadding
              sx={{
                /* The same lit link as the bar's, in the drawer's colours:
                   a soft tint of the organization's primary. */
                "& .Mui-selected": {
                  color: "primary.main",
                  "& .MuiListItemText-primary": { fontWeight: 600 },
                },
              }}
            >
              {visibleNavItems.map((item) => (
                <ListItem key={item.to} disablePadding>
                  <ListItemButton
                    selected={isCurrentNav(item, pathname)}
                    aria-current={
                      isCurrentNav(item, pathname) ? "page" : undefined
                    }
                    onClick={() => {
                      if (item.editorsOnly) clearLocal();
                      handleNav(item.to)();
                    }}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            <Box sx={{ flexGrow: 1 }} />

            {/* user info + help + admin + sign out bottom of drawer */}
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                User: {username}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Role: {userGroups.join(", ")}
              </Typography>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={handleNav("/Help")}
                sx={{ mb: 1 }}
              >
                Help
              </Button>
              {userGroups.includes("Admin") && (
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  data-tour="drawer-admin"
                  onClick={handleNav("/AdminPage")}
                  sx={{ mb: 1 }}
                >
                  Admin
                </Button>
              )}
              <Button
                fullWidth
                size="small"
                variant="contained"
                onClick={handleSignOut}
                sx={{
                  bgcolor: "#2b2b2b",
                  color: "#fff",
                  "&:hover": { bgcolor: "#1d1d1d" },
                }}
              >
                Sign Out
              </Button>
            </Box>
          </Box>
        </Drawer>
      )}

      {/* pushes page content below the fixed AppBar; must match the AppBar
          toolbar's pinned minHeight or content slides under it on phones */}
      <Toolbar sx={{ minHeight: 64 }} />

      <Container sx={{ mt: 2 }}>{children}</Container>
    </>
  );
};
